import { useForm } from "@tanstack/react-form";
import { useId } from "react";

import { t } from "../../../i18n";
import { expenseSchema } from "../../../lib/schemas";
import { trpc } from "../../../lib/trpc";
import { fieldError } from "../../../lib/validation";
import { Button, Dialog, Field, Input, Select, Stack, Switch } from "../../ui";
import type { Expense, Member } from "../types";
import { labelForMember } from "../utils";

interface EditExpenseDialogProps {
  /**
   * Settlement the expense belongs to. Required so the invalidation
   * after `updateExpense` targets the right cache key — without it
   * tRPC would invalidate every `settlement.get` regardless of id,
   * which both wastes network and races with optimistic updates the
   * row-level handlers in ExpensesTable apply.
   */
  settlementId: string;
  expense: Expense;
  members: Member[];
  categories: Array<{ id: string; name: string; defaultShared: boolean }>;
  onClose: () => void;
}

export function EditExpenseDialog({
  settlementId,
  expense,
  members,
  categories,
  onClose,
}: EditExpenseDialogProps) {
  const utils = trpc.useUtils();
  const update = trpc.settlement.updateExpense.useMutation({
    onSuccess: () => {
      utils.settlement.get.invalidate({ settlementId });
      onClose();
    },
  });

  const form = useForm({
    defaultValues: {
      date: expense.date,
      description: expense.description,
      amount: expense.amount,
      categoryId: expense.categoryId as string | null,
      shared: expense.isShared,
      paidBy: expense.paidByUserId,
    },
    validators: { onChange: expenseSchema, onSubmit: expenseSchema },
    onSubmit: ({ value }) =>
      update.mutate({
        expenseId: expense.id,
        date: value.date,
        description: value.description.trim(),
        amount: value.amount,
        categoryId: value.categoryId,
        isShared: value.shared,
        paidByUserId: value.paidBy,
      }),
  });

  const formId = useId();

  return (
    <Dialog
      open
      onClose={onClose}
      title={t.settlement.expenses.editDialog.title}
      description={t.settlement.expenses.editDialog.description}
      footer={
        <>
          <Button onClick={onClose}>{t.common.cancel}</Button>
          <Button type="submit" form={formId} variant="primary" isLoading={update.isPending}>
            {t.common.save}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
      >
        <Stack gap={4}>
          <form.Field name="description">
            {(field) => (
              <Field
                label={t.settlement.expenses.addDialog.descriptionLabel}
                required
                error={fieldError(field)}
              >
                <Input
                  autoFocus
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder={t.settlement.expenses.addDialog.descriptionPlaceholder}
                  invalid={!!fieldError(field)}
                />
              </Field>
            )}
          </form.Field>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <form.Field name="date">
              {(field) => (
                <Field
                  label={t.settlement.expenses.addDialog.dateLabel}
                  required
                  error={fieldError(field)}
                >
                  <Input
                    type="date"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    invalid={!!fieldError(field)}
                  />
                </Field>
              )}
            </form.Field>
            <form.Field name="amount">
              {(field) => (
                <Field
                  label={t.settlement.expenses.addDialog.amountLabel}
                  required
                  error={fieldError(field)}
                >
                  <Input
                    type="number"
                    step="0.01"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    placeholder={t.settlement.expenses.addDialog.amountPlaceholder}
                    suffix={t.settlement.expenses.addDialog.amountSuffix}
                    invalid={!!fieldError(field)}
                  />
                </Field>
              )}
            </form.Field>
          </div>
          <form.Field name="categoryId">
            {(field) => (
              <Field label={t.settlement.expenses.addDialog.categoryLabel}>
                <Select
                  value={field.state.value ?? ""}
                  onChange={(e) => field.handleChange(e.target.value || null)}
                >
                  <option value="">{t.settlement.expenses.addDialog.noCategory}</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}{" "}
                      {t.settlement.expenses.addDialog.categoryOptionSuffix(c.defaultShared)}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
          </form.Field>
          <form.Field name="paidBy">
            {(field) => (
              <Field label={t.settlement.expenses.addDialog.paidByLabel} error={fieldError(field)}>
                <Select
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                >
                  {members.map((m) => {
                    const lbl = labelForMember(m, members);
                    return (
                      <option key={m.userId} value={m.userId} title={lbl.tooltip}>
                        {lbl.name}
                      </option>
                    );
                  })}
                </Select>
              </Field>
            )}
          </form.Field>
          <form.Field name="shared">
            {(field) => (
              <Switch
                checked={field.state.value}
                onCheckedChange={(v) => field.handleChange(v)}
                label={t.settlement.expenses.addDialog.sharedSwitchLabel}
                hint={
                  field.state.value
                    ? t.settlement.expenses.addDialog.sharedHintShared
                    : t.settlement.expenses.addDialog.sharedHintPrivate
                }
              />
            )}
          </form.Field>
        </Stack>
      </form>
    </Dialog>
  );
}
